/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import camelCase from 'lodash/camelCase';
import cloneDeep from 'lodash/cloneDeep';
import isPlainObject from 'lodash/isPlainObject';
import mergeWith from 'lodash/mergeWith';

import {commonOptions} from '../options/common.options';
import {dwService} from '../services/dw.service';
import {urlHelper} from './url.helper';

const directiveOptions = dwService.preference('directives') || {};

/**
 * Component helper
 */
export const optionsHelper = {
  /**
   * jQuery regex used to test JSON content
   *
   * @readonly
   */
  JSON_REGEX: /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/,

  /**
   * Regex used to test amb's directives custom `srcset`
   *
   * @readonly
   */
  SRCSET_REGEX: /^(?:\w+\s*,\s*)?(?:\w+\s+(?:xs|sm|md|lg|xl|xxl|mobile|tablet|desktop|\d+w)(?:\s*,\s*|$))+$/,

  /**
   * @param {string} directiveName
   * @param {string} presetName
   */
  getServerOptions(directiveName, presetName) {
    return cloneDeep(directiveOptions[directiveName + 'Options'] || {})[presetName] || null;
  },

  /**
   * Gets plugin options by the given plugin inline options from DOM element.
   *
   * @param {object}                 optionContainer Plugin options container
   * @param {string|string[]|object} options         Plugin inline options from DOM element
   * @param {object}                 params          Parameters passed from the compiler
   * @param {HTMLElement}            params.element
   * @param {Attr}                   params.attr
   *
   * @returns {object} The plugin options
   */
  getOptions(optionContainer, options, params) {
    const directiveName = params?.attr && camelCase(params.attr.name.replace(/data-/i, ''));

    if (Array.isArray(options)) {
      return options
        .filter((option) => !!option)
        .reduce((options, entryOptions) => {
          this.merge(options, this.getOptions(optionContainer, entryOptions, params));

          if (directiveName && typeof entryOptions === 'string') {
            for (const presetName of entryOptions.split(/\s*,\s*/)) {
              const serverOptions = this.getServerOptions(directiveName, presetName);

              if (serverOptions) {
                options = this.decorate(options, serverOptions);
              }
            }
          }

          return options;
        }, {})
      ;
    } else if (typeof options === 'string') {
      options = options.trim();

      if (this.JSON_REGEX.test(options)) {
        try {
          return this.getOptions(optionContainer, JSON.parse(options), params);
        } catch (error) {}
      }

      if (options.indexOf(',') !== -1) {
        return this.getOptions(optionContainer, options.split(','), params);
      } else if (options.includes('?')) {
        return this.getOptions(optionContainer, [options.substr(0, options.indexOf('?')), urlHelper.queryToObject(options)], params);
      } else {
        const presetName = options;

        if (Object.hasOwn(optionContainer, presetName)) {
          options = optionContainer[presetName];

          if (typeof options === 'function') {
            options = options.call(optionContainer, params);
          } else {
            options = cloneDeep(options || {});
          }
        }

        if (directiveName) {
          const serverOptions = this.getServerOptions(directiveName, presetName);

          if (serverOptions) {
            if (isPlainObject(options)) {
              this.merge(options, serverOptions);
            } else {
              options = serverOptions;
            }
          }
        }
      }
    }

    return isPlainObject(options) ? options : {};
  },

  /**
   * Gets list of preset name used by the given `inlineOptions`
   *
   * @param {object}                 options       Plugin options container
   * @param {string|string[]|object} inlineOptions Plugin inline options from DOM element
   *
   * @returns {string[]} List of preset names used by the give `inlineOptions`
   */
  listOptions(options, inlineOptions) {
    const presets = [];

    if (Array.isArray(inlineOptions)) {
      return inlineOptions.reduce((inlineOptions, entryOptions) => {
        return inlineOptions.concat(this.listOptions(options, entryOptions));
      }, []);
    } else if (typeof inlineOptions === 'string') {
      inlineOptions = inlineOptions.trim();

      if (this.JSON_REGEX.test(inlineOptions)) {
        try {
          return this.listOptions(options, JSON.parse(inlineOptions));
        } catch (error) {}
      }

      if (inlineOptions.indexOf(',') !== -1) {
        return this.listOptions(options, inlineOptions.split(','));
      } else if (inlineOptions.includes('?')) {
        return this.listOptions(options, inlineOptions.substr(0, inlineOptions.indexOf('?')));
      } else if (Object.hasOwn(options, inlineOptions)) {
        presets.push(inlineOptions);
      }
    }

    return presets;
  },

  /**
   * Gets all value from matched media query with current device
   *
   * @param {MediaQueryList[]} mediaQueries List of registered media query
   *
   * @returns {any} Values which match with current device
   */
  getMatchedMediaValue(mediaQueries) {
    let matchedMediaValue = null;

    for (const mediaQuery of mediaQueries) {
      if (mediaQuery.matches) {
        if (isPlainObject(mediaQuery.value) && isPlainObject(matchedMediaValue)) {
          this.merge(matchedMediaValue, mediaQuery.value);
        } else {
          matchedMediaValue = cloneDeep(mediaQuery.value);
        }
      }
    }

    return matchedMediaValue;
  },

  /**
   * Parse and correct user configured responsive using media query syntax
   *
   * @param {object} options User configured options
   *
   * @returns {object} Fixed user configured options
   */
  fixResponsiveOptions(options = {}) {
    const mediaWidthRegex = /^(min|max)?(?:-?width)?:?\s*(\d+)(cm|mm|in|px|pt|pc|em|ex|ch|rem|vw|vh|vmin|vmax|%)?$/i;

    for (const option in options) {
      const match = option.match(mediaWidthRegex);

      if (match) {
        const media = `(${match[1] === 'min' ? 'min' : 'max'}-width: ${match[2]}${match[3] || 'px'})`;

        options[media] = options[option];
        delete options[option];
      }
    }

    const sortedMedia = Object.keys(options).sort((a, b) => {
      const bValue = b.match(/(\d+)/)[1];

      return b.includes('max-width') ? -bValue : (b.includes('min-width') ? bValue : 0);
    });

    return sortedMedia.reduce((sortedOptions, query) => {
      sortedOptions[query] = options[query];

      return sortedOptions;
    }, {});
  },

  /**
   * Tests user configured `srcset` is valid or not.
   *
   * @param {string} srcset User configured srcset
   *
   * @returns {boolean} `true` if valid
   */
  isValidSrcset(srcset) {
    return typeof srcset === 'string' && this.SRCSET_REGEX.test(srcset);
  },

  /**
   * Parse user configured `srcset` into media query syntax.
   *
   * -----
   *
   * Currently, this method follow responsive flow.
   *
   * ```text
   *  0 <---- mobile <----------- tablet <-------------------- desktop
   *  xs <------- sm <----- md <----- lg <----- xl <----- xxl <----- ∞
   *
   *  xxl: (max-width: 1400px) -> match screen size below 1400px
   *  xl:  (max-width: 1200px) -> match screen size below 1200px
   *  lg:  (max-width: 992px)  -> match screen size below 992px
   *  md:  (max-width: 768px)  -> match screen size below 768px
   *  sm:  (max-width: 576px)  -> match screen size below 576px
   *  xs:  (max-width: 0px)    -> match none
   *
   *  desktop: match all
   *  tablet: (max-width: 992px) -> match screen size below 992px
   *  mobile: (max-width: 576px) -> match screen size below 576px
   * ```
   *
   * @todo Allow parse `srcset` follow mobile first flow.
   *
   * @param {string} srcset User configured srcset
   *
   * @returns {object} The parsed media query with values
   */
  parseSrcset(srcset) {
    if (this.isValidSrcset(srcset)) {
      const breakpoints = {
        ...commonOptions.breakpoints,

        mobile: commonOptions.breakpoints.sm,
        tablet: commonOptions.breakpoints.lg,
        desktop: Infinity,
        all: Infinity,
      };

      return srcset
        .split(',')
        .map((subset) => {
          subset = subset.trim().split(/\s+/).reverse();

          if (subset.length === 1) {
            subset.unshift('all');
          }

          return subset;
        })
        .sort((a, b) => {
          const aValue = breakpoints[a[0]] || parseFloat(a[0]);
          const bValue = breakpoints[b[0]] || parseFloat(b[0]);

          return aValue > bValue ? -bValue : bValue;
        })
        .reduce((set, [breakpoint, id], index) => {
          if (breakpoint === 'all' || breakpoint === 'desktop') {
            set.all = id;
          } else if (breakpoints[breakpoint]) {
            set[`(max-width: ${breakpoints[breakpoint]}px)`] = id;
          } else if (breakpoint.substr(-1) === 'w') {
            set[`(max-width: ${parseInt(breakpoint)}px)`] = id;
          }

          return set;
        }, {})
      ;
    }

    return {};
  },

  /**
   * Merge options with other options
   *
   * @param {object} options
   * @param {object} otherOptions
   *
   * @returns {object}
   */
  merge(options = {}, otherOptions = {}) {
    const mergedOptions = mergeWith(options, otherOptions, (objValue, srcValue) => {
      if (Array.isArray(objValue)) {
        return srcValue;
      }
    });

    return this.decorate(mergedOptions, otherOptions);
  },

  /**
   * Decorator options start with `@` character.
   *
   * @private
   *
   * @param {object} options
   * @param {object} otherOptions
   *
   * @returns {object}
   */
  decorate(options, otherOptions) {
    for (const [property, configs] of Object.entries(otherOptions)) {
      if (property[0] === '@') {
        const decoratorName = camelCase(`decorate-${property.substring(1)}`);

        if (typeof this[decoratorName] === 'function') {
          options = this[decoratorName](options, otherOptions, configs);
        }
      }
    }

    return options;
  },

  /**
   * `@override` decorator
   *
   * @decorator
   * @private
   *
   * @param {object} options
   * @param {object} otherOptions
   * @param {any}    properties
   *
   * @returns {object}
   */
  decorateOverride(options, otherOptions, properties) {
    if (properties === true) {
      options = otherOptions;
    } else {
      [].concat(properties)
        .forEach((property) => {
          if (Object.hasOwn(otherOptions, property)) {
            options[property] = otherOptions[property];
          } else {
            delete options[property];
          }
        })
      ;
    }

    delete options['@override'];

    return options;
  },
};
